import { Box, Typography, ToggleButtonGroup, ToggleButton, Paper, Divider } from "@mui/material";
import { YearlyProfit } from "./charts/YearlyProfit";
import { YearlyOverview } from "./charts/YearlyOverview";
import { MonthlyOverview } from "./charts/MonthlyOverview";
import { CategorySunBurst } from "./charts/CategorySunBurst";
import { useState } from "react";
import { AnalyticsOutlined, CalendarToday, DateRange } from "@mui/icons-material";

const CURRENT_YEAR = new Date().getFullYear();
const CURRENT_MONTH = new Date().getMonth() + 1;
const YEARS = Array.from({ length: 10 }, (_, i) => CURRENT_YEAR - 5 + i);
const MONTHS = [
    { value: 1, label: '1月' },
    { value: 2, label: '2月' },
    { value: 3, label: '3月' },
    { value: 4, label: '4月' },
    { value: 5, label: '5月' },
    { value: 6, label: '6月' },
    { value: 7, label: '7月' },
    { value: 8, label: '8月' },
    { value: 9, label: '9月' },
    { value: 10, label: '10月' },
    { value: 11, label: '11月' },
    { value: 12, label: '12月' }
];

export const Statistics = () => {
    const [viewType, setViewType] = useState('yearly'); // yearly, monthly
    const [year, setYear] = useState(CURRENT_YEAR);
    const [month, setMonth] = useState(CURRENT_MONTH);

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f9fafb' }}>
            {/* 左侧边栏 */}
            <Paper
                elevation={0}
                sx={{
                    width: 260,
                    borderRadius: 0,
                    bgcolor: 'white',
                    borderRight: '1px solid #e5e7eb',
                    position: 'sticky',
                    top: 0,
                    height: '100vh',
                    overflowY: 'auto',
                    flexShrink: 0
                }}
            >
                {/* 标题 */}
                <Box sx={{ p: 3, borderBottom: '1px solid #e5e7eb' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                        <Box sx={{
                            p: 1,
                            borderRadius: 2,
                            bgcolor: 'primary.main',
                            color: 'white',
                            display: 'flex'
                        }}>
                            <AnalyticsOutlined sx={{ fontSize: 22 }} />
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1f2937' }}>
                            统计面板
                        </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                        收支数据分析
                    </Typography>
                </Box>

                {/* 统计类型切换 */}
                <Box sx={{ p: 3, pb: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#6b7280', mb: 1.5, display: 'block' }}>
                        统计类型
                    </Typography>
                    <ToggleButtonGroup
                        value={viewType}
                        exclusive
                        fullWidth
                        size="small"
                        onChange={(_, v) => v && setViewType(v)}
                        sx={{
                            '& .MuiToggleButton-root': {
                                border: '1px solid #e5e7eb',
                                borderRadius: 1.5,
                                py: 0.75,
                                fontSize: 13,
                                fontWeight: 500,
                                textTransform: 'none',
                                color: '#6b7280',
                                '&.Mui-selected': {
                                    bgcolor: 'primary.main',
                                    color: 'white',
                                    borderColor: 'primary.main',
                                    '&:hover': { bgcolor: 'primary.dark' }
                                },
                                '&:hover': {
                                    bgcolor: 'grey.50'
                                }
                            }
                        }}
                    >
                        <ToggleButton value="yearly" sx={{ flex: 1 }}>
                            <DateRange sx={{ fontSize: 16, mr: 0.5 }} />年度
                        </ToggleButton>
                        <ToggleButton value="monthly" sx={{ flex: 1 }}>
                            <CalendarToday sx={{ fontSize: 16, mr: 0.5 }} />月度
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                <Divider sx={{ mx: 3 }} />

                {/* 年份选择 */}
                <Box sx={{ p: 3, pb: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#6b7280', mb: 1.5, display: 'block' }}>
                        选择年份
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
                        {YEARS.map((y) => (
                            <ToggleButton
                                key={y}
                                value={y}
                                selected={year === y}
                                onClick={() => setYear(y)}
                                sx={{
                                    border: '1px solid #e5e7eb',
                                    borderRadius: 1.5,
                                    py: 0.75,
                                    fontSize: 13,
                                    fontWeight: 500,
                                    textTransform: 'none',
                                    color: '#6b7280',
                                    '&.Mui-selected': {
                                        bgcolor: 'primary.main',
                                        color: 'white',
                                        borderColor: 'primary.main'
                                    },
                                    '&:hover': {
                                        bgcolor: year === y ? 'primary.dark' : 'grey.50'
                                    }
                                }}
                            >
                                {y}
                            </ToggleButton>
                        ))}
                    </Box>
                </Box>

                {/* 月份选择 - 仅月度统计显示 */}
                {viewType === 'monthly' && (
                    <>
                        <Divider sx={{ mx: 3 }} />
                        <Box sx={{ p: 3, pb: 2 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#6b7280', mb: 1.5, display: 'block' }}>
                                选择月份
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
                                {MONTHS.map((m) => (
                                    <ToggleButton
                                        key={m.value}
                                        value={m.value}
                                        selected={month === m.value}
                                        onClick={() => setMonth(m.value)}
                                        sx={{
                                            border: '1px solid #e5e7eb',
                                            borderRadius: 1.5,
                                            py: 0.75,
                                            fontSize: 12,
                                            fontWeight: 500,
                                            textTransform: 'none',
                                            color: '#6b7280',
                                            '&.Mui-selected': {
                                                bgcolor: 'primary.main',
                                                color: 'white',
                                                borderColor: 'primary.main'
                                            },
                                            '&:hover': {
                                                bgcolor: month === m.value ? 'primary.dark' : 'grey.50'
                                            }
                                        }}
                                    >
                                        {m.label}
                                    </ToggleButton>
                                ))}
                            </Box>
                        </Box>
                    </>
                )}

                <Divider sx={{ mx: 3 }} />

                {/* 当前选择摘要 */}
                <Box sx={{ p: 3 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#6b7280', mb: 1.5, display: 'block' }}>
                        当前查看
                    </Typography>
                    <Box sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: 'grey.50',
                        border: '1px solid #e5e7eb'
                    }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1f2937' }}>
                            {viewType === 'yearly' ? `${year}年度` : `${year}年${month}月`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {viewType === 'yearly' ? '年度统计数据' : '月度统计数据'}
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            {/* 右侧内容区 */}
            <Box sx={{ flex: 1, p: 3, overflowY: 'auto' }}>
                {/* 年度统计 */}
                {viewType === 'yearly' && (
                    <>
                        <YearlyOverview year={year} />
                        <Box sx={{ mb: 3 }}>
                            <YearlyProfit year={year} />
                        </Box>
                        <Box>
                            <CategorySunBurst year={year} dataUrl="/api/statistic/yearly_category/" />
                        </Box>
                    </>
                )}

                {/* 月度统计 */}
                {viewType === 'monthly' && (
                    <>
                        <MonthlyOverview year={year} month={month} />
                        <Box>
                            <CategorySunBurst year={year} month={month} dataUrl="/api/statistic/monthly_category_tree/" />
                        </Box>
                    </>
                )}
            </Box>
        </Box>
    );
};
